import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { API_BASE } from "../lib/api";
import { naira, formatDate, statusBadgeClass, statusLabel, formatApiError } from "../lib/format";
import { Card, Loading } from "../components/UI";
import SignaturePad from "../components/SignaturePad";
import LeaseRenewalModal from "../components/lease/LeaseRenewalModal";
import LeaseDeleteModal from "../components/lease/LeaseDeleteModal";
import LeaseFinancialsForm from "../components/lease/LeaseFinancialsForm";
import {
  Download, Send, CreditCard, RefreshCw, X, FileSignature, ArrowLeft,
  Calendar, Save, ShieldCheck, Trash2, ArrowRight, CheckCircle2,
} from "lucide-react";
import { useAuth } from "../lib/auth";

export default function LeaseDetail() {
  const { leaseId } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [lease, setLease] = useState(null);
  const [signing, setSigning] = useState(null); // role being signed
  const [sigData, setSigData] = useState("");
  const [editingDates, setEditingDates] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [leasePayments, setLeasePayments] = useState([]);
  const [editingFin, setEditingFin] = useState(false);
  const [renewing, setRenewing] = useState(false);
  const [deletingOpen, setDeletingOpen] = useState(false);

  const reload = () => {
    api.get(`/leases/${leaseId}`).then(r => {
      setLease(r.data);
      setStartDate(r.data.lease_start_date || "");
      setEndDate(r.data.lease_end_date || "");
    });
    api.get(`/payments?lease_id=${leaseId}`)
      .then(r => setLeasePayments(r.data))
      .catch(() => setLeasePayments([]));
  };
  useEffect(() => { reload(); }, [leaseId]);

  if (!lease) return <Loading/>;

  const sign = async () => {
    if (!sigData) { toast.error("Please sign first"); return; }
    try {
      await api.post(`/leases/${leaseId}/sign`, { signature_data_url: sigData, role: signing });
      toast.success(`Signed as ${signing}`);
      setSigning(null); setSigData("");
      reload();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const send = async () => {
    try { await api.post(`/leases/${leaseId}/send`); toast.success("Sent to tenant"); reload(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const createPayLink = () => {
    toast.info("Online payment gateways have been removed. Record the payment manually from the Payments page once the tenant uploads a receipt.");
    nav("/payments");
  };

  const cancel = async () => {
    if (!confirm("Cancel this unsigned lease? It will be marked Cancelled but kept for audit.")) return;
    try { await api.post(`/leases/${leaseId}/cancel`); toast.success("Cancelled"); reload(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const saveDates = async () => {
    try {
      await api.patch(`/leases/${leaseId}/dates`, { lease_start_date: startDate, lease_end_date: endDate });
      toast.success("Lease dates updated");
      setEditingDates(false);
      reload();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const adminOverrideSign = async () => {
    const reason = prompt("Admin override sign — this will stamp every empty signer slot and mark the lease completed.\nProvide a reason (audit-logged, e.g. 'Historical paper-lease backfill'):");
    if (!reason || !reason.trim()) { toast.error("Reason is required"); return; }
    if (!confirm("Confirm admin override sign? This action is irreversible.")) return;
    try {
      await api.post(`/leases/${leaseId}/admin-override-sign`, { reason });
      toast.success("Lease signed via admin override");
      reload();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const resendLease = async () => {
    const customEmail = prompt(
      "Resend lease to which email?\n\nLeave blank to use the tenant's email on file.\nType a custom email to override (e.g. relative, lawyer, alt address).",
      ""
    );
    if (customEmail === null) return;
    try {
      const { data } = await api.post(`/leases/${leaseId}/resend`, { email: (customEmail || "").trim() });
      toast.success(`Lease emailed to ${data.sent_to}${data.override ? " (custom address)" : ""}`);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const downloadUrl = `${API_BASE}/leases/${leaseId}/download`;
  const isAdmin = user.role === "admin";
  // Eligibility for the admin override-sign button: end-date in June 2026 or earlier AND created before July 1 2026.
  const endDateStr = lease.lease_end_date || "";
  const createdStr = (lease.created_at || "").slice(0, 10);
  const overrideEligible = isAdmin
    && endDateStr && endDateStr <= "2026-06-30"
    && (!createdStr || createdStr < "2026-07-01")
    && lease.lease_status !== "completed"
    && lease.lease_status !== "cancelled"
    && !lease.admin_override;
  const canManagerSign = ["manager","admin"].includes(user.role) && !lease.manager_signature_url && lease.lease_status !== "cancelled";
  const canLawyerSign = ["lawyer","admin"].includes(user.role) && !lease.lawyer_signature_url && lease.lease_status !== "cancelled";
  const canTenantSign = ["tenant","manager","admin"].includes(user.role) && !lease.tenant_signature_url && lease.lease_status !== "cancelled";
  const canReplace = isAdmin && lease.lease_status !== "cancelled";
  const canSend = ["manager","admin"].includes(user.role) && lease.lease_status === "draft";
  const canResend = ["manager","admin"].includes(user.role) && lease.lease_status !== "cancelled";
  const canPayLink = ["manager","admin"].includes(user.role);

  // Renewal window: enabled when today is within ±31 days of lease_end_date.
  const inRenewalWindow = (() => {
    if (!lease.lease_end_date) return false;
    try {
      const end = new Date(lease.lease_end_date + "T00:00:00Z");
      const today = new Date(new Date().toISOString().slice(0,10) + "T00:00:00Z");
      const diffDays = Math.round((end - today) / 86400000);
      return Math.abs(diffDays) <= 31;
    } catch { return false; }
  })();
  const canRenew = ["manager","admin"].includes(user.role)
    && !["renewed","cancelled","draft","sent_to_tenant"].includes(lease.lease_status)
    && inRenewalWindow;

  const canCancel = isAdmin && ["draft", "sent_to_tenant"].includes(lease.lease_status);
  const canDelete = isAdmin;
  const canEditFinancials = ["admin","manager"].includes(user.role) && lease.lease_status === "draft";

  const SignerRow = ({ label, name, sigField, signRole, allowed }) => (
    <div className="flex items-center justify-between py-3 border-b border-[#E5EBE8] last:border-0">
      <div>
        <div className="font-semibold text-sm">{label}</div>
        <div className="text-xs text-[#5E7166]">{name}</div>
      </div>
      <div className="flex items-center gap-2">
        {lease[sigField] ? (
          <span className="badge badge-green">Signed</span>
        ) : (
          <span className="badge badge-gold">Pending</span>
        )}
        {allowed && <button onClick={() => setSigning(signRole)} className="btn-outline-emerald text-xs" data-testid={`sign-${signRole}-btn`}><FileSignature size={12}/> Sign</button>}
        {!allowed && lease[sigField] && canReplace && (
          <button
            onClick={() => {
              if (confirm(`Replace the existing ${label} signature? This is audit-logged and the lease status won't change.`)) {
                setSigning(signRole);
              }
            }}
            className="btn-ghost text-xs"
            title="Replace existing signature (admin only, audit-logged)"
            data-testid={`replace-${signRole}-btn`}
          >
            <RefreshCw size={11}/> Replace
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <button onClick={() => nav(-1)} className="btn-ghost text-sm" data-testid="back-btn"><ArrowLeft size={14}/> Back</button>

      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 fade-up">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#C9A227]">LEASE {lease.id.slice(0,8)}</div>
          <h1 className="font-serif text-3xl font-bold">{lease.tenant?.full_name}</h1>
          <p className="text-[#5E7166] text-sm">{lease.unit?.unit_name} • {lease.unit_type_name}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <a href={downloadUrl} target="_blank" rel="noreferrer" className="btn-outline-emerald" data-testid="download-lease-btn"><Download size={14}/> PDF</a>
          {canSend && <button onClick={send} className="btn-gold" data-testid="send-lease-btn"><Send size={14}/> Send to Tenant</button>}
          {!canSend && canResend && <button onClick={resendLease} className="btn-outline-emerald" data-testid="resend-lease-btn"><Send size={14}/> Resend Lease</button>}
          {canPayLink && <button onClick={createPayLink} className="btn-gold" data-testid="create-paylink-btn"><CreditCard size={14}/> Generate Invoice</button>}
          {canRenew && <button onClick={() => setRenewing(true)} className="btn-outline-emerald" data-testid="renew-lease-btn"><RefreshCw size={14}/> Renew</button>}
          {overrideEligible && <button onClick={adminOverrideSign} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#7A6810] text-white hover:bg-[#5C4F0C] text-sm" title="Admin override — bulk-sign historical lease (≤ Jun 2026 end date)" data-testid="admin-override-sign-btn"><ShieldCheck size={14}/> Admin Override Sign</button>}
          {lease.admin_override && <span className="badge badge-gold text-[10px]" title={lease.admin_override_reason}>Override-signed</span>}
          {canCancel && <button onClick={cancel} className="btn-ghost text-[#C92A2A]" data-testid="cancel-lease-btn" title="Cancel this unsigned lease (admin only)">Cancel</button>}
          {canDelete && <button onClick={() => setDeletingOpen(true)} className="btn-ghost text-[#C92A2A]" data-testid="delete-lease-btn" title="Delete this lease record (admin only, signed leases need typed confirmation)"><Trash2 size={14}/> Delete</button>}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card title="Lease Status" className="lg:col-span-1">
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-[#5E7166]">Current</span><span className={statusBadgeClass(lease.lease_status)}>{statusLabel(lease.lease_status)}</span></div>
            <div className="flex justify-between items-center"><span className="text-[#5E7166]">Start</span>
              {editingDates ? <input type="date" value={startDate} onChange={e=>setStartDate(e.target.value)} className="input-modav text-xs py-1 max-w-[55%]" data-testid="lease-start-input"/> : <span className="font-semibold">{formatDate(lease.lease_start_date)}</span>}
            </div>
            <div className="flex justify-between items-center"><span className="text-[#5E7166]">End</span>
              {editingDates ? <input type="date" value={endDate} onChange={e=>setEndDate(e.target.value)} className="input-modav text-xs py-1 max-w-[55%]" data-testid="lease-end-input"/> : <span className="font-semibold">{formatDate(lease.lease_end_date)}</span>}
            </div>
            {isAdmin && (
              <div className="pt-2 flex gap-2 justify-end">
                {editingDates ? (
                  <>
                    <button onClick={() => { setEditingDates(false); setStartDate(lease.lease_start_date || ""); setEndDate(lease.lease_end_date || ""); }} className="btn-ghost text-xs">Cancel</button>
                    <button onClick={saveDates} className="btn-gold text-xs inline-flex items-center gap-1" data-testid="save-lease-dates"><Save size={12}/> Save dates</button>
                  </>
                ) : (
                  <button onClick={() => setEditingDates(true)} className="btn-outline-emerald text-xs inline-flex items-center gap-1" data-testid="edit-lease-dates"><Calendar size={12}/> Edit dates</button>
                )}
              </div>
            )}
            <div className="flex justify-between"><span className="text-[#5E7166]">Use</span><span className="font-semibold text-right">{lease.use_of_apartment || "—"}</span></div>
            {lease.paystack_payment_link && <div className="flex justify-between"><span className="text-[#5E7166]">Legacy Pay Link</span><span className="text-xs text-[#C92A2A]">(deprecated)</span></div>}
          </div>
        </Card>

        <Card
          title="Financial Terms"
          className="lg:col-span-2"
          action={
            canEditFinancials && !editingFin && (
              <button onClick={() => setEditingFin(true)} className="btn-outline-emerald text-xs inline-flex items-center gap-1" data-testid="edit-financials-btn">
                <Save size={12}/> Edit Terms
              </button>
            )
          }
        >
          {editingFin ? (
            <LeaseFinancialsForm
              lease={lease}
              onCancel={() => setEditingFin(false)}
              onSaved={() => { setEditingFin(false); reload(); }}
            />
          ) : (
            <table className="w-full text-sm">
              <tbody>
                <tr><td className="py-2 text-[#5E7166]">Rent</td><td className="py-2 text-right font-semibold">{naira(lease.rent_amount)}</td></tr>
                <tr><td className="py-2 text-[#5E7166]">Caution Fee</td><td className="py-2 text-right">{naira(lease.caution_fee)}</td></tr>
                <tr><td className="py-2 text-[#5E7166]">Agreement Fee</td><td className="py-2 text-right">{naira(lease.agreement_fee)}</td></tr>
                <tr><td className="py-2 text-[#5E7166]">Service Charge</td><td className="py-2 text-right">{naira(lease.service_charge)}</td></tr>
                <tr><td className="py-2 text-[#5E7166]">Commission ({lease.commission_rate}%)</td><td className="py-2 text-right">{naira(lease.commission_amount)}</td></tr>
                <tr className="border-t border-[#E5EBE8]"><td className="pt-3 font-bold">TOTAL PAYABLE</td><td className="pt-3 text-right naira-total text-xl">{naira(lease.total_payable)}</td></tr>
                {lease.financial_override && (
                  <tr><td colSpan={2} className="pt-2 text-[10px] text-[#A0801C] italic">Custom terms — differs from standard pricing for this unit type.</td></tr>
                )}
              </tbody>
            </table>
          )}
        </Card>
      </div>

      <Card title="Signatures" action={<WorkflowReminder lease={lease}/>}>
        <SignerRow label="Landlord" name="Mr. Seun Odusanya" sigField="landlord_signature_url" signRole="landlord" allowed={user.role === "admin" && !lease.landlord_signature_url}/>
        <SignerRow label="Tenant" name={lease.tenant?.full_name} sigField="tenant_signature_url" signRole="tenant" allowed={canTenantSign}/>
        <SignerRow label="Property Manager" name="Arch. Keshinro" sigField="manager_signature_url" signRole="manager" allowed={canManagerSign}/>
        <SignerRow label="Legal Officer" name="Barr. Adekunle" sigField="lawyer_signature_url" signRole="lawyer" allowed={canLawyerSign}/>
        {lease.witness_name && <SignerRow label="Witness" name={lease.witness_name} sigField="witness_signature_url" signRole="witness" allowed={["manager","admin"].includes(user.role) && !lease.witness_signature_url}/>}
      </Card>

      <Card title="Document Preview" testid="lease-preview-card">
        <p className="text-sm text-[#5E7166] mb-3">Click PDF button above to download/print the formal Tenancy Agreement with all current signatures.</p>
        <iframe src={`${downloadUrl}#view=FitH`} className="w-full border border-[#E5EBE8] rounded-lg" style={{height: 600}} title="Lease PDF"/>
      </Card>

      <Card title={`Payments on this lease (${leasePayments.length})`} testid="lease-payments-card">
        {(() => {
          const paid = leasePayments.filter(p => p.payment_status === "paid").reduce((s, p) => s + (Number(p.amount) || 0), 0);
          const pct = lease.total_payable ? Math.min(100, Math.round((paid / lease.total_payable) * 100)) : 0;
          return (
            <div className="mb-4">
              <div className="flex justify-between items-baseline text-sm">
                <span className="text-[#5E7166]">Paid so far</span>
                <span className="font-bold text-[#0E5C3A]">{naira(paid)} / <span className="text-[#5E7166] font-normal">{naira(lease.total_payable)}</span> <span className="text-[#C9A227] ml-1 text-xs">({pct}%)</span></span>
              </div>
              <div className="h-2 bg-[#E5EBE8] rounded-full mt-2 overflow-hidden">
                <div className="h-full bg-gradient-to-r from-[#0E5C3A] to-[#C9A227]" style={{ width: `${pct}%` }}/>
              </div>
            </div>
          );
        })()}
        {leasePayments.length === 0 ? (
          <div className="py-6 text-center text-[#5E7166] text-sm">No payments recorded against this lease yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm table-modav" data-testid="lease-payments-table">
              <thead><tr><th>Reference</th><th>Type</th><th>Amount</th><th>Status</th><th>Channel</th><th>Date</th></tr></thead>
              <tbody>
                {leasePayments.map(p => (
                  <tr key={p.id} data-testid={`lease-payment-${p.id}`}>
                    <td className="font-mono text-[10px]">{p.paystack_reference || p.id.slice(0,8)}</td>
                    <td className="capitalize">{(p.payment_type || "").replace("_", " ")}</td>
                    <td className="font-semibold">{naira(p.amount)}</td>
                    <td><span className={statusBadgeClass(p.payment_status)}>{statusLabel(p.payment_status)}</span></td>
                    <td className="text-[#5E7166] text-xs">{p.channel || "—"}</td>
                    <td className="text-[#5E7166] text-xs">{p.payment_date ? formatDate(p.payment_date) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {signing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="card-modav p-6 w-full max-w-xl" data-testid="signature-modal">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-serif text-xl font-bold">Sign as {statusLabel(signing)}</h3>
              <button onClick={() => setSigning(null)}><X size={20}/></button>
            </div>
            <SignaturePad onChange={setSigData}/>
            <div className="flex gap-2 mt-4">
              <button onClick={() => setSigning(null)} className="btn-ghost flex-1 justify-center">Cancel</button>
              <button onClick={sign} className="btn-gold flex-1 justify-center" disabled={!sigData} data-testid="confirm-sign-btn">Confirm Signature</button>
            </div>
          </div>
        </div>
      )}

      {deletingOpen && (
        <LeaseDeleteModal
          lease={lease}
          onClose={() => setDeletingOpen(false)}
          onDeleted={() => nav("/leases")}
        />
      )}

      {renewing && (
        <LeaseRenewalModal
          lease={lease}
          onClose={() => setRenewing(false)}
          onCreated={(newLease) => { setRenewing(false); nav(`/leases/${newLease.id}`); }}
        />
      )}
    </div>
  );
}


/**
 * Tiny status pill that surfaces the canonical signing order
 * (Tenant → Manager → Lawyer) without blocking out-of-order signing.
 * Shows the next missing signer in the workflow, or a "✓ Fully signed"
 * confirmation when the lease is complete.
 */
function WorkflowReminder({ lease }) {
  // Cancelled / renewed: terminal — surface that clearly
  if (lease.lease_status === "cancelled") return null;
  if (lease.lease_status === "renewed") {
    return (
      <span
        className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold bg-[#5E7166]/10 text-[#5E7166]"
        data-testid="workflow-reminder"
      >
        Lease renewed
      </span>
    );
  }
  if (lease.admin_override) {
    return (
      <span
        className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold bg-[#FDF6E3] text-[#A0801C]"
        title={lease.admin_override_reason || "Admin override-signed"}
        data-testid="workflow-reminder"
      >
        <ShieldCheck size={12}/> Admin override-signed
      </span>
    );
  }

  // Canonical signing order
  const ORDER = [
    { key: "tenant_signature_url", label: "Tenant" },
    { key: "manager_signature_url", label: "Manager" },
    { key: "lawyer_signature_url", label: "Lawyer" },
  ];
  const missing = ORDER.filter((s) => !lease[s.key]);

  // Status is the source of truth: if the lease is `completed`, it counts as
  // fully signed even when signatures were captured on paper (legacy backfill).
  if (lease.lease_status === "completed" || missing.length === 0) {
    return (
      <span
        className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold bg-[#0E5C3A]/10 text-[#0E5C3A]"
        data-testid="workflow-reminder"
        title={missing.length > 0 ? "Lease is marked completed (paper-signed or migrated)" : "All three digital signatures captured"}
      >
        <CheckCircle2 size={12}/> Fully signed
      </span>
    );
  }

  const [next, ...rest] = missing;
  return (
    <span
      className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold bg-[#FDF6E3] text-[#A0801C] border border-[#EBDDA7]"
      data-testid="workflow-reminder"
      title="Suggested signing order — out-of-order signing is allowed for override / paper-lease cases."
    >
      <span className="opacity-70 uppercase tracking-wider text-[9px]">Next</span>
      <span>{next.label}</span>
      {rest.length > 0 && (
        <>
          <ArrowRight size={11} className="opacity-50"/>
          <span className="opacity-60 font-normal">{rest.map((r) => r.label).join(" → ")}</span>
        </>
      )}
    </span>
  );
}
